/**
 * Centralized invoice calculation engine.
 *
 * Single source of truth used by:
 *   - InvoiceDialog (UI breakdown + save payload)
 *   - createInvoiceFromCase (immediate invoice from a practice)
 *   - Any future caller that needs deterministic totals
 *
 * Two modes:
 *
 *   NET mode (importoLordo = false)
 *     The operator enters NET / taxable amounts on the main field and on
 *     every manual row. Final total = net + contributo + IVA + Art.15.
 *
 *   GROSS mode (importoLordo = true, "Lordo IVA inclusa")
 *     The operator enters GROSS (IVA-included) amounts on the main field
 *     AND on every manual row. The engine REVERSE-CALCULATES the taxable
 *     portion so that the final total reconciles to the gross amount the
 *     operator entered, with IVA / contributo "scorporati" internally.
 *
 *     The reverse formula:
 *       gross = taxable * (1 + contributoRate) * (1 + ivaRate)
 *       taxable = gross / divisor   where divisor = (1+c)*(1+v)
 *
 * The function is pure and side-effect free. All inputs are coerced to
 * numbers; non-numeric inputs become 0. Output amounts are rounded to 2
 * decimals.
 */

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Parse a value into a finite JS number.
 *
 * Accepts:
 *  - number / null / undefined / ""
 *  - JS standard strings "123.45"
 *  - Italian-format strings "1.234,56" (thousand "." + decimal ",")
 *
 * Italian strings can be in transit while the operator is typing into the
 * dialog inputs (e.g. row.amount === "100,50" until the field blurs). The
 * engine must produce the right number in both states.
 */
const toNum = (n) => {
  if (n === null || n === undefined || n === "") return 0;
  if (typeof n === "number") return Number.isFinite(n) ? n : 0;
  let s = String(n).trim();
  if (!s) return 0;
  // Italian format detection: contains "," → strip thousand "." then swap ","
  if (s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  }
  const v = parseFloat(s);
  return Number.isFinite(v) ? v : 0;
};

/**
 * Compute every total the UI/PDF/save payload needs.
 *
 * @param {object} input
 * @param {number|string} input.imponibileInput        Main amount as entered
 *                                                    by the operator (gross
 *                                                    in LORDO mode, net in
 *                                                    NET mode).
 * @param {Array<{amount: number|string, description?: string}>} input.manualRows
 *        Free-text rows. Each `amount` is in the same mode as `imponibileInput`.
 * @param {boolean} input.importoLordo                 GROSS mode if true.
 * @param {number|string} input.ivaPercentage          e.g. 22 (means 22%).
 * @param {boolean} input.contributoEnabled            Auto 4% contributo.
 * @param {number|string} [input.contributoIntegrativoManual]
 *        Used only when `contributoEnabled` is false; the operator-typed
 *        amount of contributo to apply (added to taxable base for IVA).
 * @param {number|string} input.speseArt15Input        Operator-typed Art.15
 *        amount (always exempt from IVA). The €2 bollo is added on top
 *        automatically when this value exceeds €77,47.
 * @returns {object} A full breakdown ready for UI render / payload save.
 */
export function computeInvoiceTotals({
  imponibileInput,
  manualRows = [],
  importoLordo = false,
  ivaPercentage,
  contributoEnabled = true,
  contributoIntegrativoManual = 0,
  speseArt15Input = 0,
  shippingExpensesInput = 0,
} = {}) {
  const imponibileRaw = toNum(imponibileInput);
  const iva = toNum(ivaPercentage);
  const ivaRatio = iva / 100;
  const contributoRate = contributoEnabled ? 0.04 : 0;

  // Reverse divisor used in LORDO mode. Guarded against division by zero:
  // if IVA is 0 and contributo disabled, divisor === 1 → gross == net.
  const divisor = (1 + contributoRate) * (1 + ivaRatio) || 1;

  // ── Art.15 + bollo automatico (computed BEFORE the reverse-calc) ─────────
  //
  // The €2 bollo is added automatically when the operator-typed Art.15
  // amount exceeds €77,47.
  //
  // CRITICAL: in LORDO mode, the gross amounts entered by the operator
  // (main field + manual rows) represent the FINAL INVOICE TOTAL —
  // bolli/Art.15 included. So before scorporating IVA/contributo we
  // must REMOVE the Art.15 portion from the gross pool. Otherwise the
  // engine would add Art.15 ON TOP and the final total would exceed
  // what the operator entered (e.g. €100 lordo + €2 bollo → wrong €102).
  // The reduction is distributed across main + manual rows
  // proportionally to their gross value, so that EACH item's "amountNet"
  // reflects what's really being invoiced after bolli are accounted for.
  const speseArt15Raw = toNum(speseArt15Input);
  const bolloAutomatico = speseArt15Raw > 77.47 ? 2.0 : 0;
  const speseArt15Total = round2(speseArt15Raw + bolloAutomatico);

  // Raw operator-entered gross per item (LORDO mode only; in NET mode
  // these are net amounts so no reduction takes place).
  const rowsRawGross = manualRows.map((r) => toNum(r.amount));
  const grossPoolRaw = round2(
    imponibileRaw + rowsRawGross.reduce((s, v) => s + v, 0)
  );

  let mainGrossAfterArt15 = imponibileRaw;
  let rowsGrossAfterArt15 = rowsRawGross.slice();

  if (importoLordo && speseArt15Total > 0 && grossPoolRaw > 0) {
    // Clamp the reduction to the available pool (defensive: if Art.15
    // exceeds the entered gross the operator must fix the input; we
    // just won't push the pool below zero here).
    const reduction = Math.min(speseArt15Total, grossPoolRaw);
    if (imponibileRaw > 0) {
      mainGrossAfterArt15 = round2(
        imponibileRaw - reduction * (imponibileRaw / grossPoolRaw)
      );
    }
    rowsGrossAfterArt15 = rowsRawGross.map((amt) =>
      round2(amt - reduction * (amt / grossPoolRaw))
    );
  }

  // ── Main item ────────────────────────────────────────────────────────────
  const imponibilePrincipaleNet = importoLordo
    ? round2(mainGrossAfterArt15 / divisor)
    : round2(imponibileRaw);

  // ── Manual rows ──────────────────────────────────────────────────────────
  // For each row we expose BOTH the operator-entered amount (`amountInput`)
  // AND the scorporated net amount (`amountNet`). Persisting both lets the
  // dialog reopen the original input, while the PDF/back end can rely on
  // `amountNet` for a coherent imponibile sum. In LORDO mode `amountNet`
  // is computed AFTER the Art.15 reduction, so the sum of all amountNet
  // values + contributo + IVA + Art.15 reconciles to the entered gross
  // pool (modulo the whole-euro rounding convention).
  const manualRowsBreakdown = manualRows.map((row, i) => {
    const amountInput = rowsRawGross[i];
    const grossForCalc = importoLordo ? rowsGrossAfterArt15[i] : amountInput;
    const amountNet = importoLordo
      ? round2(grossForCalc / divisor)
      : round2(amountInput);
    return {
      description: row.description || "",
      amountInput,                 // ORIGINAL gross/net typed by operator (UI echo)
      amount: amountNet,           // scorporated NET → safe for PDF/back end
      amountNet,                   // explicit alias for clarity
      isAdvanceDeduction: row.isAdvanceDeduction === true,
      sourceInvoiceId: row.sourceInvoiceId || null,
    };
  });
  const manualRowsTotalInput = round2(
    manualRowsBreakdown.reduce((s, r) => s + r.amountInput, 0)
  );
  const manualRowsTotalNet = round2(
    manualRowsBreakdown.reduce((s, r) => s + r.amountNet, 0)
  );

  // ── Aggregate taxable + contributo + IVA ─────────────────────────────────
  const totalTaxableNet = round2(imponibilePrincipaleNet + manualRowsTotalNet);
  const contributoIntegrativo = contributoEnabled
    ? round2(totalTaxableNet * 0.04)
    : round2(toNum(contributoIntegrativoManual));
  const baseIVA = round2(totalTaxableNet + contributoIntegrativo);
  const ivaAmount = round2(baseIVA * ivaRatio);

  // ── Subtotal + rounding to whole euro (UX convention) ────────────────────
  // 11/06/2026 — Spese di Spedizione: addebito additivo NON soggetto a IVA.
  // Si somma al subtotale come Art.15 ma non rientra nella base imponibile.
  // Stampato in PDF solo se > 0 (gestito dal generator). Default 0 = no-op.
  const shippingExpensesAmount = round2(toNum(shippingExpensesInput));
  const subtotale = round2(baseIVA + ivaAmount + speseArt15Total + shippingExpensesAmount);
  const cents = round2(subtotale - Math.floor(subtotale));
  let arrotondamento = 0;
  if (cents > 0.005) {
    arrotondamento =
      cents >= 0.5 ? round2(1 - cents) : -round2(cents);
  }
  const totale = round2(subtotale + arrotondamento);

  return {
    // Modes / inputs echo
    importoLordo,
    ivaPercentage: iva,
    contributoEnabled,
    // Imponibile
    imponibilePrincipaleNet,
    manualRowsTotalNet,
    manualRowsTotalInput,
    totalTaxableNet,
    // VAT chain
    contributoIntegrativo,
    baseIVA,
    ivaAmount,
    // Art.15
    speseArt15: speseArt15Raw,           // original (without bollo)
    bolloAutomatico,
    speseArt15Total,                     // sum used in subtotale
    // Shipping expenses (11/06/2026) — non-VAT additive
    shippingExpensesAmount,
    // Final
    subtotale,
    arrotondamento,
    totale,
    // Manual rows with both views (for save payload + PDF)
    manualRowsBreakdown,
  };
}
